import { useState, useEffect } from "react";
import {
  Box,
  Alert,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Stack,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ImageIcon from "@mui/icons-material/Image";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import adminAPI, { AdminUser, UserRateLimits } from "../admin.api";

interface UserRateLimitsTabProps {
  user: AdminUser;
}

function UserRateLimitsTab({ user }: UserRateLimitsTabProps) {
  const [rateLimits, setRateLimits] = useState<UserRateLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRateLimits();
  }, [user._id]);

  const fetchRateLimits = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminAPI.getUserRateLimits(user._id);
      setRateLimits(data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to fetch rate limits");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !rateLimits) {
    return (
      <Alert severity="error">
        {error || "Failed to load rate limit information"}
      </Alert>
    );
  }

  const usagePercentage = (rateLimits.imagesInLast24h / rateLimits.limit) * 100;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = rateLimits.isLimitReached;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Info Alert */}
      <Alert severity="info" icon={<ImageIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          AI Image Generation Rate Limits (Tiered Based on 30-Day Usage)
        </Typography>
        <Typography variant="body2" component="div">
          • Regular users (≤50 images in last 30 days): <strong>20 images per 24 hours</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • Medium usage (&gt;50 images in last 30 days): <strong>5 images per 24 hours</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • High usage (&gt;100 images in last 30 days): <strong>3 images per 24 hours</strong>
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 1 }}>
          The daily limit uses a rolling 24-hour window, not a calendar day reset.
        </Typography>
      </Alert>

      {/* Status Alert */}
      {isAtLimit && (
        <Alert severity="error" icon={<WarningIcon />}>
          <strong>Rate Limit Reached:</strong> This user cannot generate images until their oldest image expires from the 24-hour window.
        </Alert>
      )}

      {!isAtLimit && isNearLimit && (
        <Alert severity="warning">
          <strong>Near Limit:</strong> This user is approaching their rate limit.
        </Alert>
      )}

      {!isAtLimit && !isNearLimit && (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          This user has capacity remaining for image generation.
        </Alert>
      )}

      {/* Stats Grid */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  TOTAL IMAGES GENERATED (LIFETIME)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {rateLimits.totalImagesGenerated.toLocaleString()}
                </Typography>
                <Chip
                  label={
                    rateLimits.limit === 20
                      ? "Regular Tier (20/24h)"
                      : rateLimits.limit === 5
                      ? "Medium Usage Tier (5/24h)"
                      : "High Usage Tier (3/24h)"
                  }
                  size="small"
                  color={
                    rateLimits.limit === 20
                      ? "success"
                      : rateLimits.limit === 5
                      ? "warning"
                      : "error"
                  }
                  sx={{ alignSelf: "flex-start" }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  CURRENT USAGE (LAST 24 HOURS)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {rateLimits.imagesInLast24h} / {rateLimits.limit}
                </Typography>
                <Box sx={{ width: "100%", mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usagePercentage, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "grey.300",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: isAtLimit
                          ? "error.main"
                          : isNearLimit
                          ? "warning.main"
                          : "success.main",
                        borderRadius: 4,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {usagePercentage.toFixed(1)}% of limit used
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  USAGE (LAST 30 DAYS)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {rateLimits.imagesInLast30Days?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Determines current tier limit
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  REMAINING CAPACITY
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: isAtLimit ? "error.main" : "success.main" }}>
                  {rateLimits.remaining}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rateLimits.remaining === 0 ? "No images available" : `${rateLimits.remaining} image${rateLimits.remaining !== 1 ? "s" : ""} available`}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {rateLimits.resetInfo && (
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" />
                    NEXT AVAILABLE SLOT
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {rateLimits.resetInfo.resetsInHours}h {rateLimits.resetInfo.resetsInMinutes}m
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(rateLimits.resetInfo.resetsAt).toLocaleString()}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default UserRateLimitsTab;
